import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, ArrowRight
} from 'lucide-react';
import { getDeals, createDeal } from '../../services/adminDealFlowApi';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const CapitalRaises: React.FC = () => {
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newDeal, setNewDeal] = useState({ title: '', type: 'fund', raise_target: '', summary: '' });
  const navigate = useNavigate();

  const loadDeals = async () => {
    try {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getDeals(params);
      setDeals(res.data.data || []);
    } catch (err) {
      console.error('Failed to load deals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeals(); }, [statusFilter]);

  const handleCreate = async () => {
    try {
      const res = await createDeal({
        title: newDeal.title,
        type: newDeal.type,
        summary: newDeal.summary,
        raise_target: Number(newDeal.raise_target) || 0,
        status: 'draft',
      });
      setShowCreate(false);
      setNewDeal({ title: '', type: 'fund', raise_target: '', summary: '' });
      navigate(`/admin/capital-raises/${res.data.data.id}`);
    } catch (err) {
      console.error('Failed to create deal:', err);
    }
  };

  const filtered = deals.filter(d =>
    !search || d.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Capital Raises</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage fundraising deals and investor pipelines</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" /> New Capital Raise
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 sm:items-center">
        <div className="relative min-w-0 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="open">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="exited">Exited</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deals Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((deal) => (
            <Link key={deal.id} to={`/admin/capital-raises/${deal.id}`}>
              <Card className="border-border hover:border-foreground/30 transition cursor-pointer h-full">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-[#F0F0F0] rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-[hsl(var(--chart-2))]">
                        {deal.title?.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <Badge variant={deal.status === 'open' ? 'default' : 'secondary'} className="text-xs">
                      {deal.status === 'open' ? 'Active' : deal.status}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-[#242424] mb-1 break-words">{deal.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{deal.summary || 'No description'}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-[#F0F0F0] rounded">
                      <p className="text-xs text-muted-foreground">Target</p>
                      <p className="text-sm font-semibold text-[#242424]">{formatCurrency(deal.raise_target || 0)}</p>
                    </div>
                    <div className="p-2 bg-[#F0F0F0] rounded">
                      <p className="text-xs text-muted-foreground">Raised</p>
                      <p className="text-sm font-semibold text-[hsl(var(--success))]">{formatCurrency(deal.raised || 0)}</p>
                    </div>
                    <div className="p-2 bg-[#F0F0F0] rounded">
                      <p className="text-xs text-muted-foreground">Pipeline</p>
                      <p className="text-sm font-semibold text-[hsl(var(--chart-2))]">{deal.opportunity_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}

          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              <p>No capital raises found</p>
              <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-4">
                <Plus className="h-4 w-4 mr-2" /> Create First Raise
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>New Capital Raise</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Name *</label>
              <Input
                value={newDeal.title}
                onChange={e => setNewDeal(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Green View Apartments Fund"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Type</label>
              <Select value={newDeal.type} onValueChange={v => setNewDeal(p => ({ ...p, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fund">Fund</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="development">Development</SelectItem>
                  <SelectItem value="debt">Debt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Raise Target ($)</label>
              <Input
                type="number"
                value={newDeal.raise_target}
                onChange={e => setNewDeal(p => ({ ...p, raise_target: e.target.value }))}
                placeholder="e.g. 5000000"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Summary</label>
              <textarea
                value={newDeal.summary}
                onChange={e => setNewDeal(p => ({ ...p, summary: e.target.value }))}
                placeholder="Brief description of the raise..."
                className="w-full h-20 px-3 py-2 bg-background border border-border rounded-md text-sm resize-none"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleCreate} disabled={!newDeal.title} className="w-full sm:w-auto">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CapitalRaises;
